import { VIRTUAL_FIELDS, VIRTUAL_FIELDS_REVERSE, VIEW_MODES } from '../configs/viewSettings';

export function getViewModeFromPath(pathname) {
  if (pathname.includes('/records')) return VIEW_MODES.TABLE;
  if (pathname.includes('/cards')) return VIEW_MODES.CARDS;
  if (pathname.includes('/calendar')) return VIEW_MODES.CALENDAR;
  if (pathname.includes('/reports')) return VIEW_MODES.REPORTS;
  if (pathname.includes('/pivot')) return VIEW_MODES.PIVOT;
  if (pathname.includes('/count')) return VIEW_MODES.COUNT;
  if (pathname.includes('/history')) return VIEW_MODES.HISTORY;
  if (pathname.includes('/manual')) return VIEW_MODES.MANUAL;
  return VIEW_MODES.TABLE;
}

export function makeKey(viewMode, key, fieldId = null) {
  const baseKey = `${viewMode}.${key}`;
  return fieldId != null ? `${baseKey}:${fieldId}` : baseKey;
}

export function prepareFieldId(fieldId) {
  return VIRTUAL_FIELDS[fieldId] ?? fieldId;
}

export function restoreFieldId(fieldId) {
  return VIRTUAL_FIELDS_REVERSE[fieldId] ?? fieldId;
}

export function parseFieldWithSubType(fieldString) {
  if (!fieldString) return null;

  // Если уже объект - вернуть как есть
  if (typeof fieldString === 'object' && fieldString.type === 'field') {
    return fieldString;
  }

  // Парсим строку
  const parts = String(fieldString).split('.');
  const fieldId = parts[0];
  const subType = parts[1] || undefined;

  return {
    type: 'field',
    fieldId,
    ...(subType && { subType }),
  };
}

export function fieldWithSubTypeToString(fieldObj) {
  if (!fieldObj) return '';

  if (typeof fieldObj === 'string') {
    return fieldObj;
  }

  const { fieldId, subType } = fieldObj;
  return subType ? `${fieldId}.${subType}` : fieldId;
}

export function parseStateFromApi(data) {
  const settings = {};

  for (const flatKey in data) {
    const value = data[flatKey];

    // Если ключ без точки (например, defaultViewMode),
    // сохраняем его на верхнем уровне настроек
    if (!flatKey.includes('.')) {
      settings[flatKey] = value;
      continue;
    }

    const [viewMode, ...rest] = flatKey.split('.');

    if (!settings[viewMode]) settings[viewMode] = {};

    const optionParts = rest.join('.').split(':');

    if (optionParts.length === 2) {
      // viewMode.option:fieldId → fields[fieldId][option]
      // fieldId сохраняется как есть (#id остаётся #id)
      const [fieldOption, fieldId] = optionParts;
      if (!settings[viewMode].fields) settings[viewMode].fields = {};
      if (!settings[viewMode].fields[fieldId]) settings[viewMode].fields[fieldId] = {};
      settings[viewMode].fields[fieldId][fieldOption] = value;
    } else {
      // viewMode.option → простая опция
      const option = rest.join('.');

      // Convention: Если value это объект с fieldIds, распаковываем в массив
      if (value && typeof value === 'object' && value.fieldIds) {
        settings[viewMode][option] = value.fieldIds;
      }
      // Обратная совместимость: если пришел массив, оставляем как есть
      else if (Array.isArray(value)) {
        settings[viewMode][option] = value;
      }
      // Иначе сохраняем значение как есть
      else {
        settings[viewMode][option] = value;
      }
    }
  }

  return settings;
}

export function prepareStateToApi(settings) {
  const flatSettings = {};

  for (const viewMode in settings) {
    const viewModeSettings = settings[viewMode];

    if (typeof viewModeSettings !== 'object' || viewModeSettings === null) {
      continue;
    }

    if (viewModeSettings.fields) {
      const { fields } = viewModeSettings;
      for (const fieldId in fields) {
        const fieldOptions = fields[fieldId];
        for (const optionKey in fieldOptions) {
          const flatKey = `${viewMode}.${optionKey}:${fieldId}`;
          flatSettings[flatKey] = fieldOptions[optionKey];
        }
      }
    }

    for (const optionKey in viewModeSettings) {
      if (optionKey === 'fields') continue;
      if (optionKey === 'hasChanges') continue;

      const flatKey = `${viewMode}.${optionKey}`;
      const value = viewModeSettings[optionKey];

      if (Array.isArray(value)) {
        flatSettings[flatKey] = { fieldIds: value };
      } else {
        flatSettings[flatKey] = value;
      }
    }
  }

  return flatSettings;
}

export default {
  parseStateFromApi,
  prepareStateToApi,
  makeKey,
  prepareFieldId,
  restoreFieldId,
  parseFieldWithSubType,
  fieldWithSubTypeToString,
};
